import React from 'react';

import find from 'lodash/find';
import isArray from 'lodash/isArray';

import { classnames, color, padding, useClassnames } from '@lumapps/classnames';
import { DIRECTORIES_TAGS } from '@lumapps/directories-tags/keys';
import { DirectoryTag } from '@lumapps/directories-tags/types';
import { useDirectory } from '@lumapps/directories/hooks/useDirectory';
import { FormGroupPickerField } from '@lumapps/group-pickers/components/FormGroupPickerField';
import { Group } from '@lumapps/groups/types';
import { getCurrentInstance, getInstanceLanguages } from '@lumapps/instance/ducks/selectors';
import { GenericEntityBlock } from '@lumapps/lumx-blocks/components/GenericEntityBlock';
import { FieldValues } from '@lumapps/lumx-forms';
import { FormCheckboxField } from '@lumapps/lumx-forms/components/FormCheckboxField';
import { FormSelectTextField } from '@lumapps/lumx-forms/components/FormSelectTextField';
import { FormSubSection } from '@lumapps/lumx-forms/components/FormSubSection';
import { FormTextField } from '@lumapps/lumx-forms/components/FormTextField';
import { FormURLTextField } from '@lumapps/lumx-forms/components/FormURLTextField';
import { lumappsMicroApp } from '@lumapps/lumx/custom-icons';
import { mdiRefresh } from '@lumapps/lumx/icons';
import {
    Alignment,
    DialogHeading,
    Emphasis,
    FlexBox,
    IconButton,
    Orientation,
    Placement,
    Size,
} from '@lumapps/lumx/react';
import { FormMetadataFieldset } from '@lumapps/metadata-pickers/components/FormMetadataFieldset';
import {
    UseMetadataListWithSelected,
    useMetadataListWithSelected,
} from '@lumapps/metadata-pickers/hooks/useMetadataListWithSelected';
import { Metadata } from '@lumapps/metadata/types';
import { microAppByIdFrontOffice } from '@lumapps/micro-apps/routes';
import { useSelector } from '@lumapps/redux/react';
import { useRouter } from '@lumapps/router';
import { GLOBAL, TranslateObject, useTranslate } from '@lumapps/translations';
import { MultiLanguageFormDialog } from '@lumapps/translations-ui/components/MultiLanguageFormDialog';
import {
    currentLanguageValidatedByInstanceLanguagesSelector,
    getLanguages,
} from '@lumapps/translations/ducks/selectors';
import { isRelativeUrlValid } from '@lumapps/utils/string/isUrl';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { ENTRY_TYPES, TEMPLATE_PREFIX } from '../../constants';
import { DIRECTORY_ENTRY } from '../../keys';
import { DirectoryEntry, DirectoryEntryCreationDialogProps } from '../../types';
import { getDirectoryEntryIcon, getDirectoryEntryImage } from '../../utils';
import { DirectoryEntriesTemplateFieldset } from '../DirectoryEntriesTemplateFieldset';
import { FormDirectoryEntryImage } from '../DirectoryEntryImage/FormDirectoryEntryImage';

import './index.scss';

const CLASSNAME = 'directory-entry-creation-dialog';
/**
 * Dialog for creating or editing a directory entry for a given parent directory.
 * @param DirectoryEntryCreationDialogProps
 * @returns DirectoryEntryCreationDialog
 */
export const DirectoryEntryCreationDialog: React.FC<DirectoryEntryCreationDialogProps> = ({
    className,
    entry,
    extension,
    onOpenPicker,
    onSubmit,
    lang,
    directoryTags = [],
    canSetMetadata,
    directoryId,
    isOpen,
    status,
    hideVisibility,
    ...props
}) => {
    const { translateKey, translateObject } = useTranslate();
    const { block, element } = useClassnames(CLASSNAME);
    const languages = useSelector(getLanguages);
    const instanceLanguages = useSelector(getInstanceLanguages);
    const validatedCurrentLanguage = useSelector(currentLanguageValidatedByInstanceLanguagesSelector);
    const currentAccessedInstance = useSelector(getCurrentInstance);
    const { getUrl } = useRouter();

    const [isDeletableFromFavoritesEnabled, setIsDeletableFromFavoritesEnabled] = React.useState(
        Boolean(entry?.isInFavoriteFeedKeys && entry?.isInFavoriteFeedKeys.length > 0),
    );

    React.useEffect(() => {
        const isDeletable = Boolean(entry?.isInFavoriteFeedKeys && entry?.isInFavoriteFeedKeys.length > 0);

        setIsDeletableFromFavoritesEnabled(isDeletable);
    }, [entry?.isInFavoriteFeedKeys]);

    const isNewEntry = !entry;

    const languageToUse = (lang && instanceLanguages?.includes(lang) ? lang : undefined) || validatedCurrentLanguage;

    /**
     * Retrieve the metadata associated to the current site in order to display them as fields
     * in the dialog
     */
    const metadatas = useMetadataListWithSelected({
        params: {
            instance: currentAccessedInstance.id,
            withInheritance: true,
            rootOnly: false,
            // We need to fetch all metadatas (or at least as much as we can) in order to display them.
            maxResults: '999',
        },
        fields: ['id', 'name', 'parent', 'familyKey', 'multiple', 'customContentTypes', 'instance'],
        selectedMetadatas: entry?.metadata || [],
        fetchOnMount: isOpen && canSetMetadata,
        contentTypes: [],
    });

    /**
     * Retrieve the parent directory since there are a couple of infos that we need to make sure
     * they are up to date, specifically `directory.template`
     */
    const {
        data: completeDirectory,
        baseLoadingStatus: directoryStatus,
        refetch: directoryReset,
    } = useDirectory(
        { uid: directoryId },
        {
            enabled: isOpen,
        },
    );

    const entryType = extension ? ENTRY_TYPES.MICROAPP : entry?.type || ENTRY_TYPES.LINK;

    const isLoading =
        status === BaseLoadingStatus.loading || metadatas.isLoading || directoryStatus === BaseLoadingStatus.loading;

    const onEntrySubmit = (entry: FieldValues) => {
        const {
            tags,
            feedKeys,
            isInFavoriteFeedKeys,
            metadatas: selectedMetadatas,
            thumbnail,
            type,
            ...restOfProps
        } = entry;

        const metadata: string[] = [];

        if (selectedMetadatas) {
            /**
             * Metadatas are sent over as a flat list of ids. We go through the metadata object and we
             * flat out the list. Selected values can be either arrays or simple items, we parse them differently
             * depending on their type.
             */
            Object.values(selectedMetadatas as UseMetadataListWithSelected['selectedMetadatas'])
                .filter(Boolean)
                .forEach((met: Metadata | Metadata[]) => {
                    if (isArray(met)) {
                        met.forEach((m) => {
                            metadata.push(m.id);
                        });
                    } else {
                        metadata.push(met.id);
                    }
                });
        }

        const templateValues: DirectoryEntry['values'] = {};

        Object.keys(restOfProps).forEach((key) => {
            if (key.indexOf(TEMPLATE_PREFIX) >= 0) {
                const uuid = key.replace(TEMPLATE_PREFIX, '');
                const value = restOfProps[key];

                // Format URL type values
                if (value && find(completeDirectory?.template?.components, { uuid })?.type === 'url') {
                    // Transform from { label: {en: "..."}, url: {en: "..."} }
                    // to { en: { value: ...", label: "..." }, fr: {...} }
                    const transformedValue: { [lang: string]: { value: string; label: string } } = {};

                    const languages = new Set([...Object.keys(value.url || {}), ...Object.keys(value.label || {})]);

                    languages.forEach((lang) => {
                        const urlValue = value.url?.[lang] || '';
                        const labelValue = value.label?.[lang] || '';

                        if (urlValue !== '' || labelValue !== '') {
                            transformedValue[lang] = {
                                value: urlValue,
                                label: labelValue,
                            };
                        }
                    });

                    templateValues[uuid] = transformedValue;
                } else {
                    // For non-URL types
                    templateValues[uuid] = value;
                }
            }
        });

        const microApp = extension
            ? {
                  link: { en: getUrl(microAppByIdFrontOffice({ id: extension.id })) },
                  resourceId: extension.id,
                  thumbnail: extension.icon.en,
                  resourceProperties: {
                      componentSettings: [{ icon: extension.icon }],
                  },
                  type: ENTRY_TYPES.MICROAPP,
              }
            : {};

        onSubmit({
            ...restOfProps,
            type: type || ENTRY_TYPES.LINK,
            thumbnail: thumbnail ? thumbnail.id : undefined,
            ...microApp,
            directory: directoryId,
            tags: tags ? (tags as DirectoryTag[]).map((tag) => tag.uuid) : [],
            feedKeys: feedKeys
                ? (feedKeys as Group[] | string[]).map((group) => (typeof group === 'string' ? group : group.id))
                : [],
            isInFavoriteFeedKeys: isInFavoriteFeedKeys
                ? (isInFavoriteFeedKeys as Group[] | string[]).map((group) =>
                      typeof group === 'string' ? group : group.id,
                  )
                : [],
            metadata,
            values: templateValues,
        });
    };

    const templateValues: TranslateObject | string | string[] = {};

    /**
     * From the values that come from the backend, we add a prefix in order to
     * differentiate these values from the other ones from the directory entry.
     */
    if (entry && entry.values) {
        Object.keys(entry.values).forEach((uuid) => {
            const value = entry.values ? entry.values[uuid] : {};

            templateValues[`${TEMPLATE_PREFIX}${uuid}`] = value;

            // Format URL entries
            if (value && find(completeDirectory?.template?.components, { uuid })?.type === 'url') {
                const label: { [lang: string]: string } = {};
                const url: { [lang: string]: string } = {};

                // Type check to ensure value is an object with language keys
                if (!Array.isArray(value)) {
                    Object.keys(value).forEach((lang) => {
                        if (value[lang]) {
                            label[lang] = value[lang]?.label ?? '';
                            // Set url to value[lang].value if existing
                            // Else set url to value[lang] to keep legacy link fields working
                            url[lang] = value[lang]?.value ?? value[lang] ?? '';
                        }
                    });

                    // Add the transformed value to formValues with the template prefix
                    templateValues[`${TEMPLATE_PREFIX}${uuid}`] = { label, url };
                }
            }

            // Format TEXT entries to ensure retrocompatibility with URL entries
            if (value && find(completeDirectory?.template?.components, { uuid })?.type === 'inputText') {
                const formattedValue: { [lang: string]: string } = {};
                if (!Array.isArray(value)) {
                    Object.keys(value).forEach((lang) => {
                        if (value[lang]) {
                            // Set text to value[lang].value if previsously of type url
                            // Else set current text value
                            formattedValue[lang] = value[lang]?.value ? value[lang].value : value[lang];
                        }
                    });

                    // Add the transformed value to formValues with the template prefix
                    templateValues[`${TEMPLATE_PREFIX}${uuid}`] = { ...formattedValue };
                }
            }
        });
    }

    return (
        <MultiLanguageFormDialog
            className={block([className])}
            {...props}
            status={isLoading ? BaseLoadingStatus.loading : BaseLoadingStatus.idle}
            isOpen={isOpen}
            dialogProps={{
                ...props.dialogProps,
                preventCloseOnClick: true,
                preventCloseOnEscape: true,
                onVisibilityChange: (isVisible) => {
                    if (props.dialogProps?.onVisibilityChange) {
                        props.dialogProps?.onVisibilityChange(isVisible);
                    }

                    if (!isVisible) {
                        metadatas.reset();
                        directoryReset();
                    }
                },
            }}
            heading={
                <DialogHeading>
                    {translateKey(isNewEntry ? DIRECTORY_ENTRY.NEW_ENTRY : DIRECTORY_ENTRY.EDIT_ENTRY)}
                </DialogHeading>
            }
            form={{
                values:
                    !isLoading && entry
                        ? {
                              ...entry,
                              tags: entry ? directoryTags.filter((tag) => entry.tags?.includes(tag.uuid)) : [],
                              metadatas: metadatas ? metadatas.selectedMetadatas : {},
                              thumbnail: getDirectoryEntryImage(entry),
                              ...templateValues,
                          }
                        : undefined,
            }}
            defaultLanguage={languageToUse}
            languages={languages}
            scope={CLASSNAME}
            onSubmit={onEntrySubmit}
        >
            <FormSubSection
                title={translateKey(GLOBAL.GENERAL)}
                orientation={Orientation.vertical}
                contentWrapperProps={{ orientation: 'vertical', gap: 'huge' }}
            >
                {entryType === ENTRY_TYPES.LINK ? (
                    <FlexBox orientation={Orientation.horizontal} gap={Size.huge} hAlign={Alignment.top}>
                        <FormDirectoryEntryImage />

                        <FlexBox
                            orientation={Orientation.vertical}
                            gap={Size.huge}
                            className={element('general-fields')}
                        >
                            <FormTextField name="name" isMultiLanguage isRequired label={translateKey(GLOBAL.NAME)} />

                            <FormURLTextField
                                label={translateKey(GLOBAL.URL)}
                                name="link"
                                isMultiLanguage
                                urlValidator={isRelativeUrlValid}
                            />
                        </FlexBox>
                    </FlexBox>
                ) : (
                    <FlexBox orientation={Orientation.vertical} gap={Size.huge}>
                        <FormTextField
                            name="name"
                            controllerProps={{ defaultValue: extension ? extension.name : '' }}
                            isMultiLanguage
                            isRequired
                            label={translateKey(GLOBAL.NAME)}
                        />

                        <GenericEntityBlock
                            className={classnames(padding('all', 'big'), color('background', 'dark', 'L6'))}
                            customActions={
                                <IconButton
                                    emphasis={Emphasis.low}
                                    icon={mdiRefresh}
                                    label={translateKey(GLOBAL.REPLACE)}
                                    onClick={onOpenPicker}
                                />
                            }
                            shouldCenterTitle
                            title={translateObject(extension ? extension.name : entry?.name)}
                            thumbnail={{
                                icon: lumappsMicroApp,
                                image: translateObject(extension?.icon || getDirectoryEntryIcon(entry)) || '',
                            }}
                        />
                    </FlexBox>
                )}

                {directoryTags && directoryTags.length > 0 ? (
                    <FormSelectTextField<DirectoryTag>
                        selectionType="multiple"
                        name="tags"
                        options={directoryTags}
                        label={translateKey(DIRECTORIES_TAGS.DIRECTORY_TAGS)}
                        getOptionName={(tag) => translateObject(tag.name) as string}
                        getOptionId={(tag) => tag.uuid}
                        autoFilter
                    />
                ) : null}

                {completeDirectory ? <DirectoryEntriesTemplateFieldset directory={completeDirectory} /> : null}
            </FormSubSection>

            {canSetMetadata ? (
                <FormMetadataFieldset
                    metadatas={metadatas}
                    gap={Size.huge}
                    title={translateKey(GLOBAL.METADATA)}
                    showMultipleLevels
                    contentWrapperProps={{ orientation: 'vertical' }}
                />
            ) : null}

            {!isLoading && !hideVisibility ? (
                <FormSubSection
                    title={translateKey(GLOBAL.VISIBILITY)}
                    orientation={Orientation.vertical}
                    contentWrapperProps={{ orientation: 'vertical', gap: 'huge' }}
                    withSeparator={false}
                >
                    <FormGroupPickerField
                        name="feedKeys"
                        label={translateKey(GLOBAL.VISIBLE_BY)}
                        shouldDisplayAllGroup
                        shouldDisplayPublicGroup={currentAccessedInstance.publicContentAuthorized}
                        shouldDisplaySegments
                        selectionType="multiple"
                    />

                    <FlexBox orientation={Orientation.vertical}>
                        <FormGroupPickerField
                            name="isInFavoriteFeedKeys"
                            label={translateKey(DIRECTORY_ENTRY.IN_FAVORITE_FEEDS)}
                            shouldDisplayAllGroup
                            shouldDisplayPublicGroup={currentAccessedInstance.publicContentAuthorized}
                            shouldDisplaySegments
                            selectionType="multiple"
                            controllerProps={{
                                onValueChanged: (groups: Group[]) => {
                                    /**
                                     * The checkbox for [name="deletableFromFavorite"] should not be
                                     * enabled if there are no feeds to be included as favorites
                                     */
                                    if (groups) {
                                        setIsDeletableFromFavoritesEnabled(groups.length > 0);
                                    }
                                },
                            }}
                        />
                        <FormCheckboxField
                            name="deletableFromFavorite"
                            label={translateKey(DIRECTORY_ENTRY.IS_DELETABLE_FROM_FAVORITE)}
                            isDisabled={!isDeletableFromFavoritesEnabled}
                            style={!isDeletableFromFavoritesEnabled ? { display: 'none' } : null}
                            className={element('deletable-from-favorites')}
                            placement={Placement.TOP}
                            controllerProps={{
                                dependsOn: {
                                    fields: ['isInFavoriteFeedKeys'],
                                    generator: (values: Record<string, string[]>, currentValue: boolean) => {
                                        /**
                                         * If there are values in `isInFavoriteFeedKeys`, this checkbox should
                                         * be disabled and the value should be set to `false`.
                                         */
                                        const isInFavoriteFeedKeys = values.isInFavoriteFeedKeys || [];

                                        return isInFavoriteFeedKeys.length === 0 ? false : currentValue;
                                    },
                                },
                            }}
                        />
                    </FlexBox>
                </FormSubSection>
            ) : null}
        </MultiLanguageFormDialog>
    );
};
